import { useEffect, useMemo } from 'react';

import { InMemoryStorage } from '@ocx/data-in-memory-storage';

import { parseLulaPostMessage } from './lula-post-message.utils';
import { LAST_VISITED_PATH_KEY, LAST_VISITED_PATH_TTL_MS } from './order-ahead-provider.lula.constants';

export const useLulaStorage = (params: { iframeUrl: string | null }) => {
  const { iframeUrl } = params;

  // Register the message listener
  useEffect(() => {
    if (!iframeUrl) return;

    const handleMessage = (event: MessageEvent) => {
      const { origin } = new URL(iframeUrl);
      const message = parseLulaPostMessage(event.data);

      if (event.origin !== origin) {
        return;
      }

      if (message.type === 'NAVIGATION') {
        const {
          payload: { path },
        } = message;
        InMemoryStorage.setValue(LAST_VISITED_PATH_KEY, path, { ttlMs: LAST_VISITED_PATH_TTL_MS });
        return;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [iframeUrl]);

  // Restore last visited page in Lula iframe by appending it as a query param to the iframe URL
  const iframeUrlWithPath = useMemo(() => {
    const lastVisitedPath = InMemoryStorage.getValue<string>(LAST_VISITED_PATH_KEY);
    if (!lastVisitedPath || !iframeUrl) return iframeUrl;

    const url = new URL(iframeUrl);
    // TODO discuss with Lula if we should remove '/us' on the Lula side instead of doing it here
    const pathWithoutLocalePrefix =
      lastVisitedPath === '/us' || lastVisitedPath === '/us/' ? '/' : lastVisitedPath.replace(/^\/us(?=\/)/, '');
    url.searchParams.set('path', pathWithoutLocalePrefix);
    return url.toString();
  }, [iframeUrl]);

  return { iframeUrlWithPath };
};
